import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Paper,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Warning,
  Error as ErrorIcon,
  Info,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost } from '../services/apiClient';
import { LoggerService } from '../services/LoggerService';

interface Approval {
  id: string;
  type: string;
  entity_id: string;
  entity_summary: string;
  urgency: string;
  status: string;
  requested_by: string;
  requested_by_name: string;
  approved_by: string | null;
  comments: string | null;
  created_at: string;
  updated_at: string;
}

interface ApprovalStats {
  total_pending: number;
  critical: number;
  high: number;
  normal: number;
}

function getRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

function getUrgencyChipColor(urgency: string): 'info' | 'warning' | 'error' {
  switch (urgency) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'normal':
    default:
      return 'info';
  }
}

function getTypeChipColor(type: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' {
  switch (type) {
    case 'nav_approval':
      return 'primary';
    case 'investor_onboarding':
      return 'success';
    case 'transaction_approval':
      return 'warning';
    case 'document_approval':
      return 'secondary';
    case 'compliance_review':
      return 'info';
    default:
      return 'primary';
  }
}

function formatTypeLabel(type: string | undefined): string {
  if (!type) return 'Unknown';
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const APPROVAL_TYPES = [
  'nav_approval',
  'investor_onboarding',
  'transaction_approval',
  'document_approval',
  'compliance_review',
];

const STATUSES = ['pending', 'approved', 'rejected'];

const URGENCIES = ['normal', 'high', 'critical'];

const ApprovalsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterUrgency, setFilterUrgency] = useState<string>('all');

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);

  const [actionTargetId, setActionTargetId] = useState<string | null>(null);
  const [actionComments, setActionComments] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState<Approval | null>(null);

  useEffect(() => {
    loadApprovals();
  }, []);

  const loadApprovals = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiGet<any>('/approvals');
      const raw = Array.isArray(response) ? response : (response.items || []);
      const data: Approval[] = raw.map((item: any) => ({
        ...item,
        type: item.type || item.approval_type || '',
        requested_by_name: item.requested_by_name || item.requester_name || '',
        entity_summary: typeof item.entity_summary === 'object'
          ? (item.entity_summary?.description || JSON.stringify(item.entity_summary))
          : (item.entity_summary || ''),
      }));
      setApprovals(data);
      LoggerService.info('Approvals loaded', { count: data.length });
    } catch (err: any) {
      const message = err.message || 'Failed to load approvals';
      LoggerService.error('Error loading approvals:', err);
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredApprovals = approvals.filter((a) => {
    const matchesType = filterType === 'all' || a.type === filterType;
    const matchesStatus = filterStatus === 'all' || a.status === filterStatus;
    const matchesUrgency = filterUrgency === 'all' || a.urgency === filterUrgency;
    return matchesType && matchesStatus && matchesUrgency;
  });

  const stats: ApprovalStats = {
    total_pending: approvals.filter((a) => a.status === 'pending').length,
    critical: approvals.filter((a) => a.status === 'pending' && a.urgency === 'critical').length,
    high: approvals.filter((a) => a.status === 'pending' && a.urgency === 'high').length,
    normal: approvals.filter((a) => a.status === 'pending' && a.urgency === 'normal').length,
  };

  const handleSelectToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = useCallback(() => {
    const pendingFiltered = filteredApprovals.filter((a) => a.status === 'pending');
    if (selectedIds.size === pendingFiltered.length && pendingFiltered.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingFiltered.map((a) => a.id)));
    }
  }, [filteredApprovals, selectedIds.size]);

  const handleApproveOpen = (id: string) => {
    setActionTargetId(id);
    setActionComments('');
    setApproveDialogOpen(true);
  };

  const handleApproveSubmit = async () => {
    if (!actionTargetId) return;
    try {
      setActionSubmitting(true);
      await apiPost(`/approvals/${actionTargetId}/approve`, {
        comments: actionComments || undefined,
      });
      enqueueSnackbar('Approval granted successfully', { variant: 'success' });
      setApproveDialogOpen(false);
      setActionTargetId(null);
      setActionComments('');
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(actionTargetId);
        return next;
      });
      await loadApprovals();
      LoggerService.info('Approval granted', { id: actionTargetId });
    } catch (err: any) {
      const message = err.message || 'Failed to approve';
      LoggerService.error('Error approving:', err);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleRejectOpen = (id: string) => {
    setActionTargetId(id);
    setActionComments('');
    setRejectDialogOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!actionTargetId) return;
    if (!actionComments.trim()) {
      enqueueSnackbar('Comments are required when rejecting', { variant: 'warning' });
      return;
    }
    try {
      setActionSubmitting(true);
      await apiPost(`/approvals/${actionTargetId}/reject`, {
        comments: actionComments,
      });
      enqueueSnackbar('Approval rejected', { variant: 'info' });
      setRejectDialogOpen(false);
      setActionTargetId(null);
      setActionComments('');
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(actionTargetId);
        return next;
      });
      await loadApprovals();
      LoggerService.info('Approval rejected', { id: actionTargetId });
    } catch (err: any) {
      const message = err.message || 'Failed to reject';
      LoggerService.error('Error rejecting:', err);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleBulkApproveOpen = () => {
    if (selectedIds.size === 0) {
      enqueueSnackbar('Select at least one pending approval', { variant: 'warning' });
      return;
    }
    setActionComments('');
    setBulkApproveDialogOpen(true);
  };

  const handleBulkApproveSubmit = async () => {
    if (selectedIds.size === 0) return;
    try {
      setActionSubmitting(true);
      await apiPost('/approvals/bulk-approve', {
        ids: Array.from(selectedIds),
        comments: actionComments || undefined,
      });
      enqueueSnackbar(`${selectedIds.size} approvals granted successfully`, { variant: 'success' });
      setBulkApproveDialogOpen(false);
      setSelectedIds(new Set());
      setActionComments('');
      await loadApprovals();
      LoggerService.info('Bulk approval granted', { count: selectedIds.size });
    } catch (err: any) {
      const message = err.message || 'Failed to bulk approve';
      LoggerService.error('Error bulk approving:', err);
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleViewDetails = (approval: Approval) => {
    setDetailsTarget(approval);
    setDetailsDialogOpen(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Approvals...
        </Typography>
      </Box>
    );
  }

  if (error && approvals.length === 0) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Approvals
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" sx={{ mt: 2 }} onClick={loadApprovals}>
          Retry
        </Button>
      </Box>
    );
  }

  const pendingFiltered = filteredApprovals.filter((a) => a.status === 'pending');
  const allPendingSelected = pendingFiltered.length > 0 && selectedIds.size === pendingFiltered.length;

  return (
    <Box>
      {/* Page Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Approvals
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and manage pending approval requests across funds, investors, and transactions
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {stats.total_pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'error.main' }}>
                {stats.critical}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Critical
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                {stats.high}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                High Priority
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main' }}>
                {stats.normal}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Normal
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filterType}
                  label="Type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {APPROVAL_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {formatTypeLabel(t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={filterUrgency}
                  label="Urgency"
                  onChange={(e) => setFilterUrgency(e.target.value)}
                >
                  <MenuItem value="all">All Urgencies</MenuItem>
                  {URGENCIES.map((u) => (
                    <MenuItem key={u} value={u} sx={{ textTransform: 'capitalize' }}>
                      {u.charAt(0).toUpperCase() + u.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={handleBulkApproveOpen}
                  disabled={selectedIds.size === 0}
                >
                  Bulk Approve ({selectedIds.size})
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Approvals Table */}
      {filteredApprovals.length === 0 ? (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <Typography variant="h6" color="text.secondary">
                No approvals match the current filters
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allPendingSelected}
                    indeterminate={selectedIds.size > 0 && !allPendingSelected}
                    onChange={handleSelectAll}
                    disabled={pendingFiltered.length === 0}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Entity Summary</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Urgency</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Requested By</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Age</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApprovals.map((approval) => {
                const isPending = approval.status === 'pending';
                return (
                  <TableRow
                    key={approval.id}
                    hover
                    sx={{
                      backgroundColor:
                        approval.urgency === 'critical' && isPending
                          ? 'error.50'
                          : undefined,
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(approval.id)}
                        onChange={() => handleSelectToggle(approval.id)}
                        disabled={!isPending}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatTypeLabel(approval.type)}
                        color={getTypeChipColor(approval.type)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 300 }} noWrap>
                        {approval.entity_summary}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={
                          approval.urgency === 'critical' ? (
                            <ErrorIcon fontSize="small" />
                          ) : approval.urgency === 'high' ? (
                            <Warning fontSize="small" />
                          ) : (
                            <Info fontSize="small" />
                          )
                        }
                        label={approval.urgency.charAt(0).toUpperCase() + approval.urgency.slice(1)}
                        color={getUrgencyChipColor(approval.urgency)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
                        color={
                          approval.status === 'approved'
                            ? 'success'
                            : approval.status === 'rejected'
                            ? 'error'
                            : 'default'
                        }
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{approval.requested_by_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {getRelativeTime(approval.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewDetails(approval)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isPending && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApproveOpen(approval.id)}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRejectOpen(approval.id)}
                              >
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add optional comments for this approval.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments (optional)"
            value={actionComments}
            onChange={(e) => setActionComments(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={actionSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveSubmit}
            disabled={actionSubmitting}
          >
            {actionSubmitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this request. Comments are required.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason (required)"
            value={actionComments}
            onChange={(e) => setActionComments(e.target.value)}
            required
            error={actionComments.trim().length === 0}
            helperText={actionComments.trim().length === 0 ? 'A reason is required for rejection' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={actionSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={actionSubmitting || !actionComments.trim()}
          >
            {actionSubmitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Approve Dialog */}
      <Dialog
        open={bulkApproveDialogOpen}
        onClose={() => setBulkApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Approve</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You are about to approve {selectedIds.size} request{selectedIds.size !== 1 ? 's' : ''}.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments (optional)"
            value={actionComments}
            onChange={(e) => setActionComments(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkApproveDialogOpen(false)} disabled={actionSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleBulkApproveSubmit}
            disabled={actionSubmitting}
          >
            {actionSubmitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Approve All ({selectedIds.size})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approval Details</DialogTitle>
        {detailsTarget && (
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Type
                </Typography>
                <Chip
                  label={formatTypeLabel(detailsTarget.type)}
                  color={getTypeChipColor(detailsTarget.type)}
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Urgency
                </Typography>
                <Chip
                  label={detailsTarget.urgency.charAt(0).toUpperCase() + detailsTarget.urgency.slice(1)}
                  color={getUrgencyChipColor(detailsTarget.urgency)}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={detailsTarget.status.charAt(0).toUpperCase() + detailsTarget.status.slice(1)}
                  color={
                    detailsTarget.status === 'approved'
                      ? 'success'
                      : detailsTarget.status === 'rejected'
                      ? 'error'
                      : 'default'
                  }
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Age
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {getRelativeTime(detailsTarget.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Entity Summary
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {detailsTarget.entity_summary}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Requested By
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {detailsTarget.requested_by_name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {new Date(detailsTarget.created_at).toLocaleString()}
                </Typography>
              </Grid>
              {detailsTarget.approved_by && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Processed By
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {detailsTarget.approved_by}
                  </Typography>
                </Grid>
              )}
              {detailsTarget.comments && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Comments
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, backgroundColor: 'grey.50' }}>
                    <Typography variant="body2">{detailsTarget.comments}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {detailsTarget && detailsTarget.status === 'pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleApproveOpen(detailsTarget.id);
                }}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleRejectOpen(detailsTarget.id);
                }}
              >
                Reject
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalsPage;
